/**
 * The Settings tab: scenario tuning, run modes, config I/O, and About. Every
 * control here writes into the one HarnessSettings blob (use-settings.ts),
 * which Chat reads on the next send. Nothing here ever mutates a scenario
 * directly; the server is the only place overrides are applied, and it
 * applies them fresh on each request (packages/server/src/engine/overrides.ts),
 * so this tab can be left in any state without touching what
 * `pnpm conformance` sees, since conformance never sends overrides at all.
 */

import { useState, type ReactElement } from 'react';

import { AboutPanel } from '../components/settings/about-panel.tsx';
import { ConfigPanel } from '../components/settings/config-panel.tsx';
import { ModePanel } from '../components/settings/mode-panel.tsx';
import { ScenarioCard } from '../components/settings/scenario-card.tsx';
import { Header } from '../components/header.tsx';
import { ScenarioSelect } from '../components/scenario-select.tsx';
import type { ScenarioInfo } from '../hooks/use-chat.ts';
import type { EndlessController } from '../hooks/use-endless.ts';
import type { SettingsController } from '../hooks/use-settings.ts';
import '../styles/settings.css';

export function SettingsRoute({
  route,
  onNavigate,
  settingsCtl,
  scenarios,
  endless,
}: {
  route: string;
  onNavigate: (path: string) => void;
  settingsCtl: SettingsController;
  scenarios: ScenarioInfo[];
  endless: EndlessController;
}): ReactElement {
  const tunable = scenarios.filter((scenario) => scenario.knobs.length > 0);
  const notTunable = scenarios.filter((scenario) => scenario.knobs.length === 0);
  const [selectedId, setSelectedId] = useState<string>('');
  const selected = tunable.find((scenario) => scenario.id === selectedId) ?? tunable[0];

  return (
    <div className="settings">
      <Header route={route} onNavigate={onNavigate} />
      <main className="settings__main">
        <header className="settings__page-header">
          <p className="mono muted small">chat-stream-harness / settings</p>
          <h1>Settings</h1>
          <p className="muted">
            Tune where a resume scenario drops the connection, randomize its parameters within their
            declared ranges, inject seeded chaos, or cycle the whole catalogue forever. Every
            mechanism here is opt in: a run with nothing changed here is byte identical to today.
          </p>
        </header>

        <section className="settings__section" aria-labelledby="settings-scenario-heading">
          <h2 id="settings-scenario-heading">Scenario tuning</h2>
          {tunable.length === 0 ? (
            <p className="muted">
              The scenario catalogue has not loaded yet, or none of the loaded scenarios carry a
              knob manifest.
            </p>
          ) : (
            <>
              <div className="settings__field">
                <label className="muted small" id="settings-scenario-select-label">
                  Scenario
                </label>
                <ScenarioSelect
                  labelId="settings-scenario-select-label"
                  value={selected?.id ?? ''}
                  options={tunable.map((scenario) => ({
                    id: scenario.id,
                    proves: scenario.proves,
                  }))}
                  onChange={setSelectedId}
                />
              </div>
              {selected && (
                <ScenarioCard
                  scenario={selected}
                  values={settingsCtl.settings.overridesByScenario[selected.id] ?? {}}
                  disabled={settingsCtl.settings.randomizer.enabled}
                  onChange={(key, value) => {
                    settingsCtl.update((prev) => ({
                      ...prev,
                      overridesByScenario: {
                        ...prev.overridesByScenario,
                        [selected.id]: {
                          ...prev.overridesByScenario[selected.id],
                          [key]: value,
                        },
                      },
                    }));
                  }}
                />
              )}
            </>
          )}
          {notTunable.length > 0 && (
            <details className="settings__not-tunable">
              <summary className="muted small">Not yet tunable ({notTunable.length})</summary>
              <p className="mono muted small">
                {notTunable.map((scenario) => scenario.id).join(', ')}
              </p>
            </details>
          )}
        </section>

        <ModePanel settingsCtl={settingsCtl} scenarios={scenarios} endless={endless} />
        <ConfigPanel settingsCtl={settingsCtl} />
        <AboutPanel />
      </main>
    </div>
  );
}
